import {
  PRICING_ALLOWED_FIELDS,
  WEIGHT_KEYED_FIELDS,
  type Predicate,
  type PricingAllowedField,
  type PricingContext,
} from './predicate';

const ALLOWED_SET: ReadonlySet<string> = new Set(PRICING_ALLOWED_FIELDS);

/**
 * Pure-function predicate evaluator. Reads fields ONLY from the allowlist; any
 * non-allowlisted path returns `undefined` and the leaf predicate evaluates to
 * `false`. No `eval`, no `Function`, no dynamic accessors.
 */
export function evaluatePredicate(predicate: Predicate, ctx: PricingContext): boolean {
  switch (predicate.op) {
    case 'and':
      return predicate.clauses.every((c) => evaluatePredicate(c, ctx));
    case 'or':
      return predicate.clauses.some((c) => evaluatePredicate(c, ctx));
    case 'not':
      return !evaluatePredicate(predicate.clause, ctx);
    default:
      return evaluateLeaf(predicate, ctx);
  }
}

function evaluateLeaf(
  predicate: Exclude<Predicate, { op: 'and' | 'or' | 'not' }>,
  ctx: PricingContext,
): boolean {
  const value = readField(predicate.field, ctx);
  if (value === undefined) return false;

  switch (predicate.op) {
    case 'eq':
      return comparableEquals(predicate.field, value, predicate.value);
    case 'neq':
      return !comparableEquals(predicate.field, value, predicate.value);
    case 'in':
      return predicate.values.some((v) => comparableEquals(predicate.field, value, v));
    case 'nin':
      return !predicate.values.some((v) => comparableEquals(predicate.field, value, v));
    case 'between': {
      if (typeof value !== 'number') return false;
      const incl = predicate.inclusive ?? 'both';
      const lower_ok =
        incl === 'both' || incl === 'min' ? value >= predicate.min : value > predicate.min;
      const upper_ok =
        incl === 'both' || incl === 'max' ? value <= predicate.max : value < predicate.max;
      return lower_ok && upper_ok;
    }
    case 'gte':
      return typeof value === 'number' && value >= predicate.value;
    case 'lte':
      return typeof value === 'number' && value <= predicate.value;
    case 'gt':
      return typeof value === 'number' && value > predicate.value;
    case 'lt':
      return typeof value === 'number' && value < predicate.value;
  }
}

function comparableEquals(
  field: string,
  left: string | number | boolean | null,
  right: string | number | boolean | null,
): boolean {
  if (field === 'pet.species' && typeof left === 'string' && typeof right === 'string') {
    return normalizeSpecies(left) === normalizeSpecies(right);
  }
  return left === right;
}

function normalizeSpecies(input: string): string {
  const token = input
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  if (
    token === 'canino' ||
    token === 'canina' ||
    token === 'perro' ||
    token === 'perra' ||
    token === 'dog' ||
    token === 'dogs' ||
    token === 'canine'
  ) {
    return 'canino';
  }
  if (
    token === 'felino' ||
    token === 'felina' ||
    token === 'gato' ||
    token === 'gata' ||
    token === 'cat' ||
    token === 'cats' ||
    token === 'feline'
  ) {
    return 'felino';
  }
  return token;
}

function readField(path: string, ctx: PricingContext): string | number | boolean | null | undefined {
  if (!ALLOWED_SET.has(path)) return undefined;
  const allowed = path as PricingAllowedField;
  switch (allowed) {
    case 'pet.species':
      return ctx.pet?.species ?? undefined;
    case 'pet.sex':
      return ctx.pet?.sex ?? undefined;
    case 'pet.breed':
      return ctx.pet?.breed ?? undefined;
    case 'pet.current_weight_grams':
      return ctx.pet?.current_weight_grams ?? undefined;
    case 'pet.approximate_age_months':
      return ctx.pet?.approximate_age_months ?? undefined;
    case 'customer.id':
      return ctx.customer?.id ?? undefined;
    case 'customer.category':
      return ctx.customer?.category ?? undefined;
    case 'branch_id':
      return ctx.branch_id ?? undefined;
    case 'date':
      return ctx.date ?? undefined;
  }
}

/** True if the predicate references at least one weight-keyed field. */
export function predicateRequiresWeight(predicate: Predicate): boolean {
  switch (predicate.op) {
    case 'and':
    case 'or':
      return predicate.clauses.some(predicateRequiresWeight);
    case 'not':
      return predicateRequiresWeight(predicate.clause);
    default:
      return (WEIGHT_KEYED_FIELDS as ReadonlyArray<string>).includes(predicate.field);
  }
}
